import axios from 'axios';
import { clearSession } from './sessionStore';

/*
 * Accounts. Both ways in (Google via Emergent Auth, email + password) end in the same httpOnly `session_token` cookie,
 * so every API call just needs to carry cookies — set once here for the whole app (axios is used everywhere).
 */
axios.defaults.withCredentials = true;

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Google sign-in: send the browser to Emergent Auth; it comes back to <origin>/app#session_id=… (see AuthCallback). */
export const startGoogleSignIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/app';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const exchangeSession = async (sessionId) => (await axios.post(`${API}/auth/session`, { session_id: sessionId })).data;
export const register = async ({ email, password, name, referralCode }) => (await axios.post(`${API}/auth/register`, { email, password, name, referral_code: referralCode || null })).data;
export const login = async ({ email, password }) => (await axios.post(`${API}/auth/login`, { email, password })).data;
export const getMe = async () => (await axios.get(`${API}/auth/me`)).data;
export const logout = async () => (await axios.post(`${API}/auth/logout`)).data;

// Forgot password: the backend emails a one-time link (always "ok" — it never says whether the address has an account).
export const forgotPassword = async (email) => (await axios.post(`${API}/auth/forgot-password`, { email })).data;
export const checkResetToken = async (token) => (await axios.get(`${API}/auth/reset-password/${encodeURIComponent(token)}`)).data; // { valid, email?, name? }
export const resetPassword = async ({ token, password }) => (await axios.post(`${API}/auth/reset-password`, { token, password })).data; // signs in

// Profile settings
export const updateProfile = async (patch) => (await axios.patch(`${API}/auth/me`, patch)).data; // { name?, avatar? ('' removes), avatar_color? ('' clears), email_tips? (bool) }
/** Unsubscribe from the optional tips / product-update emails with the token from the email footer (no sign-in) → { ok, email (masked) }. */
export const unsubscribeByToken = async (token) => (await axios.post(`${API}/email/unsubscribe/${encodeURIComponent(token)}`)).data;
export const changePassword = async ({ currentPassword, newPassword }) => (await axios.post(`${API}/auth/change-password`, { current_password: currentPassword || null, new_password: newPassword })).data;
/** Everything the account owns as one zip → { blob, filename }. */
export const exportMyData = async () => {
  const res = await axios.get(`${API}/auth/me/export`, { responseType: 'blob' });
  const m = /filename="?([^";]+)"?/.exec(res.headers['content-disposition'] || '');
  return { blob: res.data, filename: (m && m[1]) || 'wannapublish-export.zip' };
};
/** Bring an export zip back into this account → import summary { publications:{added,skipped}, folders:{added,reused}, templates, blocks, fonts, business_info, merged, warnings }. */
export const importMyData = async (file) => {
  const form = new FormData();
  form.append('file', file, file.name || 'export.zip');
  return (await axios.post(`${API}/auth/me/import`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};
/** Delete the account and everything in it. Password accounts send { password }, Google-only accounts { confirm: 'DELETE' }. */
export const deleteAccount = async ({ password, confirm } = {}) => (await axios.delete(`${API}/auth/me`, { data: { password: password || null, confirm: confirm || null } })).data;

/* ------------------------------------------------------------------ */
/* Per-browser state that belongs to an account                        */
/* ------------------------------------------------------------------ */
/* Nothing secret lives in web storage: the session is the httpOnly `session_token` cookie (unreadable from JS).
 * OWNER_KEY holds only the opaque account id of the last user of this browser, compared for equality to decide whether
 * the previous account's recents / pins / open tabs must be cleared. */

const OWNER_KEY = 'wp.accountOwner';
const LOCAL_KEYS = ['wp.recentProjects', 'wp.pinnedProjects'];

/** Forget everything this browser kept for the previous account: open-publication tabs (IndexedDB), recents, pins. */
export const forgetLocalAccountState = async () => {
  await clearSession();
  LOCAL_KEYS.forEach((k) => { try { window.localStorage.removeItem(k); } catch { /* storage unavailable */ } });
};

/**
 * Called right after a successful sign-in: if a different account used this browser last, its open tabs and recents
 * are cleared so they do not resurface for the new user. Returns true when a switch happened.
 */
export const claimBrowserForUser = async (userId) => {
  let previous = null;
  try { previous = window.localStorage.getItem(OWNER_KEY); } catch { /* storage unavailable */ }
  const switched = !!previous && previous !== userId;
  if (switched) await forgetLocalAccountState();
  try { window.localStorage.setItem(OWNER_KEY, userId); } catch { /* storage unavailable */ }
  return switched;
};

/** The path to land on after signing in: /app, keeping a share link's ?share=… (or an owner's ?project=…) if the visitor came with one. */
export const afterSignInPath = (search = window.location.search) => {
  const params = new URLSearchParams(search || '');
  const share = params.get('share');
  if (share) return `/app?share=${encodeURIComponent(share)}`;
  const project = params.get('project');
  return project ? `/app?project=${encodeURIComponent(project)}` : '/app';
};

/**
 * Session expiry while working: any 401 from a data endpoint (not the auth endpoints themselves, whose 401s are normal
 * sign-in feedback) sends the user back to the landing page with a note. Registered once by AuthProvider.
 */
let unauthorizedHandler = null;
export const setUnauthorizedHandler = (fn) => { unauthorizedHandler = fn; };
/** A 403 `subscription_required` from a data endpoint = the plan lapsed while working; AuthProvider re-reads /auth/me and the gate sends the user to /subscribe. */
let subscriptionRequiredHandler = null;
export const setSubscriptionRequiredHandler = (fn) => { subscriptionRequiredHandler = fn; };
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err?.config?.url || '';
    if (err?.response?.status === 401 && url.startsWith(API) && !url.includes('/auth/') && unauthorizedHandler) unauthorizedHandler(err);
    if (err?.response?.status === 403 && url.startsWith(API) && err?.response?.data?.detail?.code === 'subscription_required' && subscriptionRequiredHandler) subscriptionRequiredHandler(err);
    return Promise.reject(err);
  },
);
